import type { PlatformGrowth } from '../../../packages/frontend-core/src/platformGrowth';
import { getEnv } from '../utils/env';
import { protectedAdminFetch, throwIfProtectedAdminResponseNotOk } from './protectedAdminApi';

type UnknownRecord = Record<string, unknown>;

export type AdminContactSubmission = {
  id: string;
  name: string | null;
  email: string;
  message: string | null;
  created_at: string | null;
  type: 'contact' | 'newsletter' | 'legacy_beta' | string;
  language: 'nl' | 'en';
  status: 'new' | 'in_progress' | 'handled' | 'archived';
  notes: string | null;
  club_name: string | null;
  team_name: string | null;
  knhb_team_code: string | null;
  legacy_beta_signup_id: string | null;
  updated_at: string | null;
  nonessential_email_unsubscribed: boolean;
};

export type AdminSystemLog = {
  id: string;
  task_start: string;
  task_end?: string | null;
  type: string;
  description: unknown;
  status?: string | null;
};

export type AdminPlatformAttentionItem = {
  key: 'email_failed' | 'email_queue_delayed' | 'scheduler_disabled' | 'jobs_failed' | 'contacts_available';
  severity: 'critical' | 'warning' | 'info';
  count: number;
  age_minutes?: number;
};

export type AdminAuthSecurity = { available: false } | {
  available: true;
  generated_at: string;
  signals_last_24h: number;
  signals_last_7d?: number;
  failed_last_24h: number;
  locked_accounts: number;
  recent: { id: string; first_seen: string; last_seen: string; signal: string; flow: string; source_ip: string | null; profile_id: string | null; occurrences: number }[];
};

export type AdminPlatformSummary = {
  security?: AdminAuthSecurity;
  planner?: { failed_last_24h: number; reasons: { reason: string; count: number }[] };
  recent?: { club: { id: string; name: string | null; created_at: string | null } | null; user: { id: string; name: string | null; created_at: string | null } | null };
  generated_at: string;
  environment: string;
  email: {
    queued: number;
    failed: number;
    sent_today: number;
    sent_this_week?: number;
    oldest_queued_at?: string | null;
    oldest_queue_minutes: number;
    last_sent_at?: string | null;
  };
  scheduler: {
    enabled: boolean;
    registered_jobs: number;
    failed_last_24h: number;
    last_job_at?: string | null;
  };
  task_queue?: {
    queued: number;
    processing: number;
  };
  operations?: {
    last_backup: {
      restored_from_backup?: boolean;
      started_at?: string | null;
      completed_at?: string | null;
      status: string;
    } | null;
    last_task: {
      name?: string | null;
      started_at?: string | null;
      completed_at?: string | null;
      status: string;
    } | null;
  };
  work: {
    contact_submissions: number;
  };
  attention_items: AdminPlatformAttentionItem[];
};

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractMessage = (payload: unknown, fallback: string): string => {
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (typeof record.error === 'string' && record.error.trim().length > 0) return record.error;
    if (typeof record.message === 'string' && record.message.trim().length > 0) return record.message;
  }
  return fallback;
};

const extractData = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in (payload as UnknownRecord)) {
    return (payload as UnknownRecord).data;
  }
  return payload;
};

const adminDashboardRequest = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await protectedAdminFetch(path, {
    ...options,
    headers: options.headers,
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    await throwIfProtectedAdminResponseNotOk(
      response,
      extractMessage(payload, `Admin dashboard API request failed (${response.status})`)
    );
  }

  return extractData(payload);
};

const extractList = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    if (Array.isArray(record.items)) return record.items as T[];
    if (Array.isArray(record.results)) return record.results as T[];
    if (Array.isArray(record.data)) return record.data as T[];
  }
  return [];
};

const extractTotal = (payload: unknown): number => {
  if (payload && typeof payload === 'object') {
    const record = payload as UnknownRecord;
    const total = record.total;
    if (typeof total === 'number' && Number.isFinite(total)) return total;
  }
  return 0;
};

export const listAdminContactSubmissionsApi = async (params?: { limit?: number; offset?: number }): Promise<AdminContactSubmission[]> => {
  const query = new URLSearchParams();
  if (typeof params?.limit === 'number') query.set('limit', String(params.limit));
  if (typeof params?.offset === 'number') query.set('offset', String(params.offset));
  const qs = query.toString();
  const payload = await adminDashboardRequest(`/api/v1/admin/contact-submissions${qs ? `?${qs}` : ''}`);
  return extractList<AdminContactSubmission>(payload);
};

export const getAdminContactSubmissionStatsApi = async (): Promise<number> => {
  const payload = await adminDashboardRequest('/api/v1/admin/contact-submissions/stats');
  return extractTotal(payload);
};

export const updateAdminContactApi = async (
  contactId: string,
  data: { name: string; email: string; status: AdminContactSubmission['status']; notes: string | null }
): Promise<AdminContactSubmission> => {
  const payload = await adminDashboardRequest(`/api/v1/admin/contacts/${encodeURIComponent(contactId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const [contact] = extractList<AdminContactSubmission>(payload);
  if (!contact) throw new Error('Contact update returned no contact');
  return contact;
};

export const deleteAdminContactApi = async (contactId: string): Promise<void> => {
  await adminDashboardRequest(`/api/v1/admin/contacts/${encodeURIComponent(contactId)}`, { method: 'DELETE' });
};

export const listAdminSystemLogsApi = async (params?: { limit?: number; offset?: number }): Promise<AdminSystemLog[]> => {
  const query = new URLSearchParams();
  if (typeof params?.limit === 'number') query.set('limit', String(params.limit));
  if (typeof params?.offset === 'number') query.set('offset', String(params.offset));
  const qs = query.toString();
  const payload = await adminDashboardRequest(`/api/v1/admin/system-logs${qs ? `?${qs}` : ''}`);
  return extractList<AdminSystemLog>(payload);
};

export const getAdminSystemLogStatsApi = async (): Promise<number> => {
  const payload = await adminDashboardRequest('/api/v1/admin/system-logs/stats');
  return extractTotal(payload);
};

export const getAdminPlatformSummaryApi = async (includeRecent = false): Promise<AdminPlatformSummary> => {
  const payload = await adminDashboardRequest(`/api/v1/admin/platform-summary${includeRecent ? '?include_recent=true' : ''}`);
  if (!payload || typeof payload !== 'object') {
    throw new Error('Platform summary returned an invalid response');
  }
  return payload as AdminPlatformSummary;
};


export type AdminRecentRegistration = { id: string; name: string | null; created_at: string | null; logo_url?: string | null; club_id?: string | null; club_name?: string | null };
export type CountryTeamCount = { country: string; clubs: number; teams: number };
export type AdminDailyOverview = {
  active_users?: { id: string; name: string | null; sign_ins: number }[];
  geography?: { growth?: PlatformGrowth; countries: CountryTeamCount[]; unknown: { clubs: number; teams: number } };
  totals: { clubs: number; users: number; teams: number; players: number; exercises: number };
  clubs: AdminRecentRegistration[];
  users: AdminRecentRegistration[];
  contacts: Pick<AdminContactSubmission, 'id' | 'name' | 'email' | 'message' | 'created_at' | 'status' | 'club_name'>[];
  open_contacts: number;
  recent_contacts: AdminRecentRegistration[];
};
export const getAdminDailyOverviewApi = async (): Promise<AdminDailyOverview> =>
  await adminDashboardRequest('/api/v1/admin/daily-overview') as AdminDailyOverview;

export const getAdminAuthSecurityApi = async (): Promise<AdminAuthSecurity> =>
  await adminDashboardRequest('/api/v1/admin/auth-security') as AdminAuthSecurity;

export type PlannerConfiguration = {
  strategy: string; variant: string; app_version: string; count: number; succeeded: number; completed: number;
  average_duration_ms: number | null; average_solver_passes: number | null; average_solver_steps: number | null;
  average_completed_steps: number | null; average_result_blocks: number | null;
  settings: {
    planned_blocks: number | null; quarters: number | null; quarter_seconds: number | null;
    interval_seconds: number | null; priority: string | null;
    historical_participation: boolean | null; prefer_regular_players: boolean | null; max_one_stand_in: boolean | null;
    max_substitutions: number | null; quarter_break_resets: boolean | null;
    bench_not_together_count: number; position_precedence_count: number; locked_positions_count: number;
    line_settings: { changes: number; max_consecutive: number | null; min_rest: number | null; max_substitutions: number | null }[];
  } | null;
};
export type PlannerTiming = {
  strategy: string; variant: string; app_version: string; outcome: string; samples: number;
  average_duration_ms: number; median_duration_ms: number; p95_duration_ms: number;
};
export type PlannerRuntimePoint = { time_bucket: string; duration_ms: number; status: 'succeeded' | 'failed' | 'warning'; count: number };
export type PlannerDay = { day: string; outcomes: Record<string, number> };
export type PlannerStatistics = {
  hours: number; since: string; generated_at: string;
  groups: {strategy: string; variant: string; app_version: string; outcome: string; count: number; average_duration_ms: number | null}[];
  reasons: {strategy: string; variant: string; reason: string; diagnosis_code?: string | null; outcome?: string; count: number}[];
  configurations?: PlannerConfiguration[];
  timings?: PlannerTiming[];
  daily?: PlannerDay[];
  runtime_points?: PlannerRuntimePoint[];
  series: {total: number; recovered: number; succeeded: number; unfinished: number};
};
export const getPlannerStatisticsApi = async (hours: number): Promise<PlannerStatistics> =>
  await adminDashboardRequest(`/api/v1/admin/planner-statistics?hours=${hours}`) as PlannerStatistics;
